import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCreditCard, faUniversity, faCog, faCheck, faExclamationTriangle,
  faInfoCircle, faEye, faEyeSlash, faSave, faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { CoordinatorPaymentSetupForm, PaymentGatewayOption } from '../types/coordinatorPayment';
import { CoordinatorPaymentService } from '../services/coordinatorPaymentService';

interface CoordinatorPaymentSetupProps {
  coordinatorEmail: string;
  coordinatorName: string;
  onSave?: (config: CoordinatorPaymentSetupForm) => void;
  onCancel?: () => void;
  showCancelButton?: boolean;
}

const CoordinatorPaymentSetup: React.FC<CoordinatorPaymentSetupProps> = ({
  coordinatorEmail,
  coordinatorName,
  onSave,
  onCancel,
  showCancelButton = true
}) => {
  const [formData, setFormData] = useState<CoordinatorPaymentSetupForm>({
    payment_gateway: 'payfast',
    payfast_sandbox: true,
    account_type: 'Cheque',
    auto_confirm_payments: false
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPassphrase, setShowPassphrase] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const paymentGatewayOptions: PaymentGatewayOption[] = [
    {
      id: 'manual',
      name: 'Bank Transfer Only',
      description: 'Participants pay via bank transfer using your banking details',
      icon: 'faUniversity'
    },
    {
      id: 'payfast',
      name: 'PayFast Only',
      description: 'Online payments through PayFast (automatic payment verification)',
      icon: 'faCreditCard',
      recommended: true
    },
    {
      id: 'both',
      name: 'Both Options',
      description: 'Offer both bank transfer and PayFast payment options',
      icon: 'faCog'
    }
  ];

  const southAfricanBanks = [
    'ABSA Bank', 'Standard Bank', 'First National Bank (FNB)', 'Nedbank',
    'Capitec Bank', 'African Bank', 'Bidvest Bank', 'Discovery Bank',
    'Investec Bank', 'Mercantile Bank', 'Sasfin Bank', 'TymeBank',
    'Bank Zero', 'Other'
  ];

  useEffect(() => {
    loadExistingConfiguration();
  }, [coordinatorEmail]);

  const loadExistingConfiguration = async () => {
    try {
      setLoading(true);
      const existingConfig = await CoordinatorPaymentService.getCoordinatorPaymentConfig(coordinatorEmail);
      
      if (existingConfig) {
        setFormData({
          payment_gateway: existingConfig.payment_gateway,
          payfast_merchant_id: existingConfig.payfast_merchant_id || '',
          payfast_merchant_key: existingConfig.payfast_merchant_key || '',
          payfast_passphrase: existingConfig.payfast_passphrase || '',
          payfast_sandbox: existingConfig.payfast_sandbox ?? true,
          bank_name: existingConfig.bank_name || '',
          account_holder: existingConfig.account_holder || '',
          account_number: existingConfig.account_number || '',
          branch_code: existingConfig.branch_code || '',
          account_type: existingConfig.account_type || 'Cheque',
          auto_confirm_payments: existingConfig.auto_confirm_payments ?? false,
          payment_instructions: existingConfig.payment_instructions || ''
        });
      }
    } catch (error) {
      console.error('Error loading existing configuration:', error);
      setError('Failed to load existing configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof CoordinatorPaymentSetupForm, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear validation errors when user starts typing
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Validate configuration
      const errors = CoordinatorPaymentService.validateConfiguration(formData);
      if (errors.length > 0) {
        setValidationErrors(errors);
        return;
      }

      // Save configuration
      await CoordinatorPaymentService.saveCoordinatorPaymentConfig(
        coordinatorEmail,
        coordinatorName,
        formData
      );

      setSuccess('Payment configuration saved successfully!');
      
      // Call onSave callback if provided
      if (onSave) {
        onSave(formData);
      }

    } catch (error) {
      console.error('Error saving payment configuration:', error);
      setError('Failed to save payment configuration. Please try again.');
    } finally {
      setSaving(false);
    }
  };



  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <FontAwesomeIcon icon={faSpinner} className="text-2xl text-primary animate-spin mr-3" />
        <span className="text-gray-600">Loading payment configuration...</span>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Configuration</h2>
        <p className="text-gray-600">
          Set up how participants will pay for events you organize. You can change these settings anytime.
        </p>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
          <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 mr-2" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
          <FontAwesomeIcon icon={faCheck} className="text-green-500 mr-2" />
          <span className="text-green-700">{success}</span>
        </div>
      )}

      {validationErrors.length > 0 && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <h4 className="font-semibold text-red-800 mb-2">Please fix the following errors:</h4>
          <ul className="list-disc list-inside text-red-700">
            {validationErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-6">
        {/* Payment Gateway Selection */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Payment Method</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {paymentGatewayOptions.map((option) => (
              <div
                key={option.id}
                className={`border rounded-lg p-4 cursor-pointer transition-all ${
                  formData.payment_gateway === option.id
                    ? 'border-primary bg-orange-50'
                    : 'border-gray-200 hover:border-gray-300'
                } ${option.recommended ? 'ring-2 ring-green-200' : ''}`}
                onClick={() => handleInputChange('payment_gateway', option.id)}
              >
                <div className="flex items-center justify-between mb-2">
                  <FontAwesomeIcon 
                    icon={option.icon === 'faUniversity' ? faUniversity : option.icon === 'faCreditCard' ? faCreditCard : faCog} 
                    className="text-xl text-primary" 
                  />
                  {option.recommended && (
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                      Recommended
                    </span>
                  )}
                </div>
                <h4 className="font-semibold text-gray-900">{option.name}</h4>
                <p className="text-sm text-gray-600 mt-1">{option.description}</p>
                <div className="mt-3">
                  <div className={`w-4 h-4 rounded-full border-2 ${
                    formData.payment_gateway === option.id
                      ? 'border-primary bg-primary'
                      : 'border-gray-300'
                  }`}>
                    {formData.payment_gateway === option.id && (
                      <FontAwesomeIcon icon={faCheck} className="text-white text-xs ml-0.5" />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Banking Details */}
        {(formData.payment_gateway === 'manual' || formData.payment_gateway === 'both') && (
          <div className="border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FontAwesomeIcon icon={faUniversity} className="mr-2 text-primary" />
              Banking Details
            </h3>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <div className="flex items-start">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-500 mr-2 mt-0.5" />
                <div className="text-sm text-yellow-800">
                  <p className="font-semibold mb-1">Manual Payment Verification Required</p>
                  <p>With bank transfers, you'll need to manually verify each payment before confirming registrations. This can delay participant confirmations and increase administrative work.</p>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bank Name *
                </label>
                <select
                  value={formData.bank_name || ''}
                  onChange={(e) => handleInputChange('bank_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                >
                  <option value="">Select your bank</option>
                  {southAfricanBanks.map((bank) => (
                    <option key={bank} value={bank}>{bank}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Account Holder Name *
                </label>
                <input
                  type="text"
                  value={formData.account_holder || ''}
                  onChange={(e) => handleInputChange('account_holder', e.target.value)}
                  placeholder="Full name as it appears on bank account"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Account Number *
                </label>
                <input
                  type="text"
                  value={formData.account_number || ''}
                  onChange={(e) => handleInputChange('account_number', e.target.value)}
                  placeholder="Account number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Branch Code *
                </label>
                <input
                  type="text"
                  value={formData.branch_code || ''}
                  onChange={(e) => handleInputChange('branch_code', e.target.value)}
                  placeholder="6-digit branch code"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Account Type
                </label>
                <select
                  value={formData.account_type || 'Cheque'}
                  onChange={(e) => handleInputChange('account_type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                >
                  <option value="Cheque">Cheque Account</option>
                  <option value="Savings">Savings Account</option>
                  <option value="Current">Current Account</option>
                </select>
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Instructions (Optional)
              </label>
              <textarea
                value={formData.payment_instructions || ''}
                onChange={(e) => handleInputChange('payment_instructions', e.target.value)}
                placeholder="Additional instructions for participants making bank transfers..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
              />
              <p className="text-xs text-gray-500 mt-1">
                These instructions will be shown to participants when they choose bank transfer payment.
              </p>
            </div>
          </div>
        )}

        {/* PayFast Configuration */}
        {(formData.payment_gateway === 'payfast' || formData.payment_gateway === 'both') && (
          <div className="border border-gray-200 rounded-lg p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <FontAwesomeIcon icon={faCreditCard} className="mr-2 text-primary" />
                PayFast Configuration
              </h3>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <div className="flex items-start">
                <FontAwesomeIcon icon={faCheck} className="text-green-500 mr-2 mt-0.5" />
                <div className="text-sm text-green-800">
                  <p className="font-semibold mb-2">Recommended: Automatic Payment Verification</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Instant payment confirmation - no manual checking required</li>
                    <li>Participants are automatically registered upon successful payment</li>
                    <li>Secure online payments with fraud protection</li>
                    <li>Professional payment experience for participants</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-start">
                <FontAwesomeIcon icon={faInfoCircle} className="text-blue-500 mr-2 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-semibold mb-1">PayFast Account Required</p>
                  <p>You need a PayFast merchant account to accept online payments. Visit <a href="https://www.payfast.co.za" target="_blank" rel="noopener noreferrer" className="underline">payfast.co.za</a> to sign up.</p>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Merchant ID *
                </label>
                <input
                  type="text"
                  value={formData.payfast_merchant_id || ''}
                  onChange={(e) => handleInputChange('payfast_merchant_id', e.target.value)}
                  placeholder="Your PayFast Merchant ID"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Merchant Key *
                </label>
                <input
                  type="text"
                  value={formData.payfast_merchant_key || ''}
                  onChange={(e) => handleInputChange('payfast_merchant_key', e.target.value)}
                  placeholder="Your PayFast Merchant Key"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Passphrase *
                </label>
                <div className="relative">
                  <input
                    type={showPassphrase ? 'text' : 'password'}
                    value={formData.payfast_passphrase || ''}
                    onChange={(e) => handleInputChange('payfast_passphrase', e.target.value)}
                    placeholder="Your PayFast Passphrase"
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassphrase(!showPassphrase)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <FontAwesomeIcon icon={showPassphrase ? faEyeSlash : faEye} />
                  </button>
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.payfast_sandbox || false}
                    onChange={(e) => handleInputChange('payfast_sandbox', e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">
                    Use PayFast Sandbox (for testing) - Uncheck for live payments
                  </span>
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Additional Settings */}
        <div className="border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Settings</h3>
          
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.auto_confirm_payments || false}
                onChange={(e) => handleInputChange('auto_confirm_payments', e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">
                Auto-confirm payments (automatically mark payments as received)
              </span>
            </label>
            <p className="text-xs text-gray-500 mt-1 ml-6">
              Only enable this if you have automated payment verification in place.
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
          {showCancelButton && onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {saving ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                Saving...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} className="mr-2" />
                Save Configuration
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CoordinatorPaymentSetup; 